import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { getInitialLanguage } from '../utils/languageDetection';
import PromoBanner from '../components/PromoBanner';
import Footer from '../components/Footer';
import './DemoStory.css';

const DEMO_VIDEOS = [
  'https://customer-assets.emergentagent.com/job_login-refer-fix/artifacts/behzm7a8_SC4.mp4',
  'https://customer-assets.emergentagent.com/job_login-refer-fix/artifacts/qa0oxnt6_SC2.mp4',
  'https://customer-assets.emergentagent.com/job_login-refer-fix/artifacts/h47p0y3i_SC1.mp4'
];

export default function DemoStory() {
  const navigate = useNavigate();
  const [language, setLanguage] = useState(getInitialLanguage());

  return (
    <div className="demo-story-page">
      <PromoBanner language={language} onLanguageChange={setLanguage} />

      <header className="demo-story-header">
        <img src="/storytales-logo.png" alt="StoryTales Logo" className="demo-story-logo" onClick={() => navigate('/')} />
        <div className="demo-story-actions">
          <Button variant="outline" className="demo-header-button" onClick={() => navigate('/')}>
            {language === 'de' ? 'Zur Startseite' : 'Back to home'}
          </Button>
          <Button className="demo-header-button demo-header-primary" onClick={() => navigate('/auth')}>
            {language === 'de' ? 'Kostenlos starten' : 'Start free'}
          </Button>
        </div>
      </header>

      <main className="demo-story-main">
        <section className="demo-story-hero">
          <div className="demo-story-copy">
            <span className="demo-kicker">
              {language === 'de' ? 'Oeffentliche Demo-Story' : 'Public demo story'}
            </span>
            <h1>
              {language === 'de'
                ? 'So fühlt sich eine Story an, wenn das Kind wirklich wiedererkennbar bleibt'
                : 'See what it feels like when the child stays truly recognizable'}
            </h1>
            <p>
              {language === 'de'
                ? 'Hier siehst du dieselbe Beispielheldin über Cover, Szenen und Stimmungen hinweg. Genau dieses Ergebnis führen wir neue Nutzer auf der Startseite hin.'
                : 'Here you can see the same sample hero across cover, scenes and moods. That is the exact outcome we promise new users on the landing page.'}
            </p>
            <div className="demo-story-pills">
              <span>{language === 'de' ? 'Kind bleibt erkennbar' : 'Child stays recognizable'}</span>
              <span>{language === 'de' ? 'Cover + Szenen + Storytext' : 'Cover + scenes + story text'}</span>
              <span>{language === 'de' ? 'In 1-2 Minuten erstellt' : 'Created in 1-2 minutes'}</span>
            </div>
          </div>

          <div className="demo-story-child-card">
            <div className="demo-child-avatar">EM</div>
            <div>
              <span className="demo-child-tag">{language === 'de' ? 'Beispielkind' : 'Sample child'}</span>
              <h2>Emma, 7</h2>
              <p>
                {language === 'de'
                  ? 'Blondes Haar, helle Augen, weiche Gesichtszüge, freundlicher Ausdruck.'
                  : 'Blonde hair, bright eyes, soft facial features, friendly expression.'}
              </p>
            </div>
          </div>
        </section>

        <section className="demo-story-timeline">
          <div className="demo-step">
            <strong>01</strong>
            <span>{language === 'de' ? 'Profil oder Foto als Startpunkt' : 'Profile or photo as starting point'}</span>
          </div>
          <div className="demo-step">
            <strong>02</strong>
            <span>{language === 'de' ? 'Story wird generiert' : 'Story gets generated'}</span>
          </div>
          <div className="demo-step">
            <strong>03</strong>
            <span>{language === 'de' ? 'Dasselbe Kind über alle Seiten' : 'Same child across all pages'}</span>
          </div>
        </section>

        <section className="demo-story-gallery">
          {DEMO_VIDEOS.map((video, index) => (
            <article key={video} className="demo-story-card">
              <span className="demo-card-label">
                {index === 0 ? (language === 'de' ? 'Cover' : 'Cover') : language === 'de' ? `Szene ${index}` : `Scene ${index}`}
              </span>
              <video src={video} autoPlay muted loop playsInline className="demo-card-video" />
              <div className="demo-card-copy">
                <h3>
                  {index === 0
                    ? (language === 'de' ? 'Emmas magisches Abenteuer' : 'Emma’s magical adventure')
                    : (language === 'de' ? 'Emma bleibt klar erkennbar' : 'Emma stays clearly recognizable')}
                </h3>
                <p>
                  {language === 'de'
                    ? 'Gesicht, Haare und Stimmung bleiben über die Story hinweg konsistent.'
                    : 'Face, hair and expression remain consistent throughout the story.'}
                </p>
              </div>
            </article>
          ))}
        </section>

        <section className="demo-story-cta">
          <div>
            <span className="demo-kicker">{language === 'de' ? 'Nächster Schritt' : 'Next step'}</span>
            <h2>
              {language === 'de'
                ? 'Starte jetzt die erste Geschichte für dein eigenes Kind'
                : 'Start the first story for your own child now'}
            </h2>
            <p>
              {language === 'de'
                ? 'Wir übernehmen Name, Alter und Thema direkt aus dem Schnellstart und führen dich ohne Umwege in die Story-Erstellung.'
                : 'We carry name, age and theme directly from the quick start into story creation.'}
            </p>
          </div>
          <Button className="demo-primary-button" onClick={() => navigate('/auth')}>
            {language === 'de' ? 'Kostenlos starten' : 'Start free'}
          </Button>
        </section>
      </main>

      <Footer language={language} />
    </div>
  );
}
